import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MapIcon from '@mui/icons-material/Map';
import type { MessageDefinition } from '@k-ucap/shared';
import { DynamicField } from '@/components/message/DynamicField.js';
import { MessageBrowser } from '@/components/message/MessageBrowser.js';
import { PreviewPanel } from '@/components/message/PreviewPanel.js';
import { RecentMessages } from '@/components/message/RecentMessages.js';
import { BulkGenerator } from '@/components/generator/BulkGenerator.js';
import { GeneratorAssetSelector } from '@/components/generator/GeneratorAssetSelector.js';
import { AssetLibrary } from '@/components/asset/AssetLibrary.js';
import { useGenerator } from '@/hooks/useGenerator.js';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { ValidationSeverity } from '@k-ucap/shared';

const steps = ['메시지 선택', '필드 입력', 'Validation 확인', 'Preview 확인', 'Send'];

export const GeneratorPage = () => {
  const navigate = useNavigate();
  const generator = useGenerator();
  const dictionaryService = useMemo(() => createMessageDictionaryService(), []);
  const [filter, setFilter] = useState({
    search: '',
    category: '',
    sourceRole: '',
    targetRole: '',
    transferMode: '',
    supportedOnly: true,
  });
  const [activeTab, setActiveTab] = useState<'generator' | 'library'>('generator');

  const filteredMessages = useMemo(() => dictionaryService.filterMessages(filter), [dictionaryService, filter]);

  const activeStep = useMemo(() => {
    if (!generator.selectedMessage) return 0;
    if (!generator.isProjectSelected) return 1;
    if (!generator.validationResult) return 1;
    if (generator.validationResult.severity !== ValidationSeverity.PASS) return 2;
    if (!generator.rawHex) return 3;
    return 4;
  }, [generator.selectedMessage, generator.isProjectSelected, generator.validationResult, generator.rawHex]);

  const handleSelectMessage = (msg: MessageDefinition) => {
    generator.selectMessage(msg);
  };

  const canSendNormal = generator.canSend && generator.validationResult?.severity !== ValidationSeverity.FAIL;
  const canSendForce =
    generator.canSend && generator.canForceSend && generator.validationResult?.severity === ValidationSeverity.FAIL;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        메시지 생성기
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)}>
          <Tab label="Generator" value="generator" />
          <Tab label="Asset Library" value="library" />
        </Tabs>
      </Box>

      {activeTab === 'generator' && (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {!generator.isProjectSelected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              프로젝트를 선택해야 메시지를 전송할 수 있습니다. Settings에서 프로젝트를 선택하세요.
            </Alert>
          )}

          <GeneratorAssetSelector
            templates={generator.availableTemplates}
            parameterSets={generator.availableParameterSets}
            datasets={generator.availableDatasets}
            selectedTemplate={generator.selectedTemplate}
            selectedParameterSet={generator.selectedParameterSet}
            selectedDataset={generator.selectedDataset}
            onSelectTemplate={generator.selectTemplate}
            onSelectParameterSet={generator.selectParameterSet}
            onSelectDataset={generator.selectDataset}
            onSaveAsTemplate={generator.saveAsTemplate}
            disabled={!generator.canSend}
          />

          <Grid container spacing={2} sx={{ height: 'calc(100vh - 320px)', minHeight: 480 }}>
            <Grid size={{ xs: 12, md: 3 }} sx={{ height: '100%' }}>
              <MessageBrowser
                messages={filteredMessages}
                selected={generator.selectedMessage}
                onSelect={handleSelectMessage}
                filter={filter}
                onFilterChange={setFilter}
                categories={dictionaryService.getCategories()}
                sourceRoles={dictionaryService.getSourceRoles()}
                targetRoles={dictionaryService.getTargetRoles()}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 5 }} sx={{ height: '100%' }}>
              <Paper sx={{ height: '100%', p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                {generator.selectedMessage ? (
                  <>
                    <Box sx={{ mb: 2 }} data-testid="message-detail-header">
                      <Typography variant="h6">
                        #{generator.selectedMessage.messageType} {generator.selectedMessage.displayName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {generator.selectedMessage.name} | {generator.selectedMessage.category} |{' '}
                        {generator.selectedMessage.sourceRole} → {generator.selectedMessage.targetRole} |{' '}
                        {generator.selectedMessage.transferMode}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip
                          label={`ACK: ${generator.selectedMessage.acknowledgementRequired ? 'Yes' : 'No'}`}
                          size="small"
                        />
                        {generator.selectedMessage.defaultPeriodMs && (
                          <Chip label={`Period: ${generator.selectedMessage.defaultPeriodMs}ms`} size="small" />
                        )}
                        {!generator.selectedMessage.supported && (
                          <Chip label="Unsupported" color="warning" size="small" />
                        )}
                      </Box>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                      {generator.selectedMessage.fields.map((field) => (
                        <Box key={field.key} sx={{ mb: 2 }}>
                          <DynamicField
                            field={field}
                            value={generator.payload[field.key]}
                            onChange={generator.updatePayload}
                            errors={
                              generator.validationResult?.issues
                                .filter((i) => i.path === field.key)
                                .map((i) => ({
                                  fieldKey: field.key,
                                  status: i.severity as ValidationSeverity,
                                  code: i.code,
                                  message: i.message,
                                })) ?? []
                            }
                            disabled={!generator.canSend}
                          />
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RestoreIcon />}
                        onClick={generator.resetPayload}
                        disabled={!generator.selectedMessage}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={generator.loadDemoValues}
                        disabled={!generator.selectedMessage}
                      >
                        Load Demo Values
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Typography color="text.secondary">왼쪽 메시지를 선택하세요.</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%' }}>
              <PreviewPanel
                message={generator.selectedMessage}
                payload={generator.payload}
                validationResult={generator.validationResult}
                rawHex={generator.rawHex}
                rawBytes={generator.rawBytes ? Array.from(generator.rawBytes) : []}
                sourceStack={generator.resolvedPayloadSource}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, my: 2 }}>
            {generator.validationResult && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={generator.validationResult.severity}
                  color={
                    generator.validationResult.severity === ValidationSeverity.PASS
                      ? 'success'
                      : generator.validationResult.severity === ValidationSeverity.FAIL
                        ? 'error'
                        : 'warning'
                  }
                />
                <Typography variant="body2">
                  PASS {generator.validationResult.issues.filter((i) => i.severity === ValidationSeverity.PASS).length}{' '}
                  / WARN{' '}
                  {generator.validationResult.issues.filter((i) => i.severity === ValidationSeverity.WARN).length} /{' '}
                  FAIL {generator.validationResult.issues.filter((i) => i.severity === ValidationSeverity.FAIL).length}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => generator.send(false)}
              disabled={!canSendNormal || generator.isSending}
            >
              Send
            </Button>
            {canSendForce && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => generator.send(true)}
                disabled={generator.isSending}
              >
                Force Send
              </Button>
            )}
            {generator.lastSentEventId && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate(`/analyzer?messageEventId=${generator.lastSentEventId}`)}
                >
                  Analyzer에서 보기
                </Button>
                {generator.selectedMessage && [101, 104, 110, 1100].includes(generator.selectedMessage.messageType) && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<MapIcon />}
                    onClick={() => {
                      const vehicleId = generator.payload.vehicleId;
                      if (vehicleId && typeof vehicleId === 'string') {
                        navigate(`/gis?vehicleId=${encodeURIComponent(vehicleId)}`);
                      } else {
                        navigate('/gis');
                      }
                    }}
                  >
                    GIS에서 보기
                  </Button>
                )}
              </>
            )}
          </Box>

          {generator.selectedMessage && (
            <BulkGenerator messageDefinition={generator.selectedMessage} payload={generator.payload} />
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <RecentMessages messages={generator.recentEvents} />
            </Grid>
          </Grid>
        </>
      )}

      {activeTab === 'library' && (
        <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 480 }}>
          <AssetLibrary
            templates={generator.availableTemplates}
            parameterSets={generator.availableParameterSets}
            datasets={generator.availableDatasets}
            onSelectTemplate={(template) => {
              setActiveTab('generator');
              generator.selectTemplate(template);
            }}
          />
        </Box>
      )}

      <Snackbar
        open={generator.snackbarOpen}
        autoHideDuration={4000}
        onClose={generator.closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={generator.sendError ? 'error' : 'success'}
          sx={{ width: '100%' }}
          onClose={generator.closeSnackbar}
        >
          {generator.sendError
            ? generator.sendError
            : generator.sendSuccess
              ? `#${generator.sendSuccess.messageType} Seq=${generator.sendSuccess.sequenceNumber} (${generator.sendSuccess.validation}) 전송 완료`
              : '전송 완료'}
        </Alert>
      </Snackbar>
    </Box>
  );
};
